import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const THEMES = {
  jewelry: { bg: "#FAFAFA", surface: "#FFFFFF", text: "#1A1918", accent: "#C5A059", border: "#E5E0D8", muted: "#8A8578" },
  men: { bg: "#F4F4F3", surface: "#FFFFFF", text: "#0F1115", accent: "#22252A", border: "#D1D5DB", muted: "#6B7280" },
  women: { bg: "#FDFBF7", surface: "#FFFFFF", text: "#1A1715", accent: "#A88B82", border: "#EAE4DC", muted: "#9A8E86" },
};

const load = (k, d) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; }
};

export function AppProvider({ children }) {
  const [storefront, setStorefront] = useState(() => load("ev_storefront", "jewelry"));
  const [country, setCountry] = useState(() => load("ev_country", null));
  const [entered, setEntered] = useState(() => load("ev_entered", false));
  const [currency, setCurrency] = useState(() => load("ev_currency", "CAD"));
  const [rates, setRates] = useState({ USD: 1 });
  const [symbols, setSymbols] = useState({});
  const [cart, setCart] = useState(() => load("ev_cart", []));
  const [wishlist, setWishlist] = useState(() => load("ev_wishlist", []));
  const [user, setUser] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { api.get("/currency/rates").then((r) => { setRates(r.data.rates); setSymbols(r.data.symbols); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/auth/me").then((r) => setUser(r.data.user)).catch(() => setUser(false)); }, []);

  useEffect(() => { localStorage.setItem("ev_storefront", JSON.stringify(storefront)); }, [storefront]);
  useEffect(() => { localStorage.setItem("ev_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("ev_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { if (country) localStorage.setItem("ev_country", JSON.stringify(country)); }, [country]);
  useEffect(() => { localStorage.setItem("ev_currency", JSON.stringify(currency)); }, [currency]);
  useEffect(() => { localStorage.setItem("ev_entered", JSON.stringify(entered)); }, [entered]);

  useEffect(() => {
    const t = THEMES[storefront];
    const root = document.documentElement;
    Object.entries(t).forEach(([k, v]) => root.style.setProperty(`--ev-${k}`, v));
  }, [storefront]);

  const money = useCallback((usd) => {
    const rate = rates[currency] || 1;
    const sym = symbols[currency] || "$";
    const val = usd * rate;
    const zero = currency === "JPY" || currency === "KRW";
    const num = zero ? Math.round(val).toLocaleString() : val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${sym}${num}`;
  }, [rates, currency, symbols]);

  const addToCart = useCallback((product, opts = {}) => {
    setCart((c) => {
      const optKey = (opts.options || []).map((o) => `${o.group}:${o.choice}`).join("|");
      const key = `${product.id}-${opts.size || ""}-${opts.color || ""}-${optKey}`;
      const idx = c.findIndex((i) => i.key === key);
      if (idx >= 0) { const n = [...c]; n[idx].qty += opts.qty || 1; return n; }
      return [...c, {
        key, id: product.id, name: product.name, slug: product.slug, storefront: product.storefront,
        image: (product.images || [])[0],
        price_usd: opts.unitPrice != null ? opts.unitPrice : (product.sale_price_usd || product.price_usd),
        options: opts.options || [], size: opts.size || null, color: opts.color || null, qty: opts.qty || 1,
      }];
    });
    setCartOpen(true);
  }, []);
  const updateQty = useCallback((key, qty) => setCart((c) => qty <= 0 ? c.filter((i) => i.key !== key) : c.map((i) => i.key === key ? { ...i, qty } : i)), []);
  const removeFromCart = useCallback((key) => setCart((c) => c.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((w) => w.find((i) => i.id === product.id)
      ? w.filter((i) => i.id !== product.id)
      : [...w, { id: product.id, name: product.name, slug: product.slug, storefront: product.storefront, image: (product.images || [])[0], price_usd: product.sale_price_usd || product.price_usd }]);
  }, []);
  const inWishlist = useCallback((id) => !!wishlist.find((i) => i.id === id), [wishlist]);

  const cartCount = cart.reduce((n, i) => n + i.qty, 0);

  const value = {
    storefront, setStorefront, country, setCountry, currency, setCurrency,
    entered, setEntered,
    rates, symbols, money, theme: THEMES[storefront],
    cart, cartCount, addToCart, updateQty, removeFromCart, clearCart, cartOpen, setCartOpen,
    wishlist, toggleWishlist, inWishlist,
    user, setUser,
  };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
